/* eslint-disable no-unused-vars */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Container,
  Fab,
  LinearProgress,
  Typography,
  makeStyles
} from '@material-ui/core';
import { Page } from 'src/components';
import { useFastMovingProducts } from 'src/hooks/fast-moving-products';
import {
  FastMovingProductSubToolbar,
  FastMovingProductTableList,
  FastMovingProductToolBar,
  MobileDataTable
} from './components';
import useUserInfo from 'src/hooks/user/use-user-info';
import { Alert } from '@material-ui/lab';
import { StockFilter } from 'src/enums/fast-moving-products';
import {
  FastMovingProductsData,
  SortData
} from 'src/redux/slices/fast-moving-products';
import { FastMovingProductTableV2 } from './components/FastMovingProductTableV2';
import {
  fastMovingHeaderKeys,
  fastMovingSortedData
} from 'src/utils/fast-moving-products';
import { isArray, isEmpty } from 'lodash';
import { DateRangeSpecific, SelectedColumn } from 'src/types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { cleanParams } from 'src/utils';
import { stringToNumberArr } from 'src/utils/stringUtils';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import useResolution from 'src/hooks/useResolution';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  productCard: {
    height: '100%'
  }
}));

export const FastMovingStockView = () => {
  const navigate = useNavigate();
  const classes = useStyles();
  const [searchParams] = useSearchParams();
  const { userBranchOptions } = useUserInfo();
  const {
    isLoading,
    fastMovingProdParams,
    fastMovingProductData,
    dataCopy,
    error,
    setFastMovingProdParams,
    getSoldTimelineParams,
    getFastMovingProducts
  } = useFastMovingProducts();
  const { xs } = useResolution();

  const queryParams = useMemo(() => {
    return Object.fromEntries(searchParams.entries());
  }, [searchParams]);

  const parsedBranchIdsFromQuery = useMemo(() => {
    return stringToNumberArr(queryParams.branch_ids);
  }, [queryParams.branch_ids]);

  const [isCategoryRequire, setIsCategoryRequire] = useState<boolean>(false);
  const [isDateRangeRequire, setIsDateRangeRequire] = useState<boolean>(false);
  const [isBranchRequire, setIsBranchRequire] = useState<boolean>(false);
  const [columnToHide, setColumnToHide] = useState<number[]>();
  const [sortFlag, setSortFlag] = useState<SortData>({});
  const [selectedShowColumn, setSelectedShowColumn] = useState<
    SelectedColumn[]
  >([]);
  const [columnTotal, setColumnTotal] = useState<any>();
  const [searchKeyword, setSearchKeyword] = useState<string>(
    queryParams?.keyword || ''
  );

  const defaultValueBranch = useMemo(() => {
    let branchIds: number[] = [];

    branchIds = userBranchOptions.map((branch) => branch?.value);
    return parsedBranchIdsFromQuery?.length
      ? parsedBranchIdsFromQuery
      : branchIds;
  }, [userBranchOptions, parsedBranchIdsFromQuery]);

  const headerKeys = useMemo(() => {
    return fastMovingHeaderKeys(fastMovingProductData);
  }, [fastMovingProductData]);

  const sortedData: any = useMemo(() => {
    return fastMovingSortedData(fastMovingProductData);
  }, [fastMovingProductData]);

  const onApplyQuery = useCallback(
    (newFilters: any) => {
      const params = new URLSearchParams({
        ...newFilters,
        branch_ids: newFilters?.branch_ids?.join(',') || ''
      });

      navigate(`?${params}`);
    },
    [navigate]
  );

  const onApplyFilterPress = useCallback(
    (columnsToShow?: SelectedColumn[]) => {
      const currentParams = { ...fastMovingProdParams };
      const statusFilter = currentParams.status_filter;
      delete currentParams.status_filter;

      let newParams = { ...currentParams };

      if (!fastMovingProdParams.category_id) {
        setIsCategoryRequire(true);
        return;
      }

      //We extract columns that contain branch ids
      const branchIds: any = columnsToShow
        ?.filter((column) => column?.value !== 0)
        .map((branch) => branch?.value)
        .filter((value) => value !== undefined);

      if (!fastMovingProdParams?.branch_ids?.length) {
        setIsBranchRequire(true);
        return;
      }
      if (
        (!fastMovingProdParams?.date_sold_from ||
          !fastMovingProdParams?.date_sold_to) &&
        !fastMovingProdParams?.preset_dates
      ) {
        setIsDateRangeRequire(true);
        return;
      }

      if (!isEmpty(branchIds) && isArray(branchIds)) {
        newParams = {
          ...newParams,
          branch_ids: branchIds
        };
      }

      getFastMovingProducts(newParams);
      setFastMovingProdParams(newParams);
      setSelectedShowColumn(columnsToShow || []);
      setSortFlag({});

      const cleanedParams = cleanParams(newParams);

      if (cleanedParams.preset_dates) {
        getSoldTimelineParams({
          preset_date: cleanedParams.preset_dates,
          from_date: undefined,
          to_date: undefined
        });
      } else {
        getSoldTimelineParams({
          preset_date: undefined,
          from_date: cleanedParams.date_sold_from,
          to_date: cleanedParams.date_sold_to
        });
      }

      onApplyQuery(cleanedParams);
    },
    [
      fastMovingProdParams,
      getFastMovingProducts,
      getSoldTimelineParams,
      onApplyQuery,
      setFastMovingProdParams
    ]
  );

  const handleErrorClose = () => {
    setIsCategoryRequire(false);
    setIsDateRangeRequire(false);
    setIsBranchRequire(false);
  };

  //has default branch for multiple selection
  useEffect(() => {
    setFastMovingProdParams((prev) => ({
      ...prev,
      keyword: searchKeyword || undefined,
      branch_ids: prev.branch_ids || defaultValueBranch
    }));
  }, [defaultValueBranch, searchKeyword, setFastMovingProdParams]);

  useEffect(() => {
    if (!isEmpty(queryParams)) {
      const branchIds = queryParams?.branch_ids
        ?.split(',')
        .map(Number)
        .filter((id) => !isNaN(id));

      if (branchIds?.length > 0) {
        userBranchOptions.filter((branch) => branchIds.includes(branch.value));
      }

      // mark setparams
      setFastMovingProdParams({
        keyword: queryParams?.keyword,
        category_id: Number(queryParams?.category_id),
        date_sold_from: queryParams?.date_sold_from,
        date_sold_to: queryParams?.date_sold_to,
        status_filter:
          queryParams?.with_sales === '1'
            ? StockFilter.WithSales
            : StockFilter.All,
        branch_ids: branchIds,
        preset_dates: queryParams?.preset_dates
      });
    }
  }, [setFastMovingProdParams, queryParams, userBranchOptions]);

  const scrollToTopWithOffset = (offset: number = 0) => {
    const target = 500;
    window.scrollTo({
      top: Math.max(target - offset, 0),
      behavior: 'smooth'
    });
  };

  return (
    <Page title="Fast Moving Products" className={classes.root}>
      <Container maxWidth={false} style={{ position: 'relative' }}>
        <Typography color="textPrimary" gutterBottom variant="h3">
          Fast Moving Products
        </Typography>

        <FastMovingProductToolBar
          loadingData={isLoading}
          initialBranchIds={defaultValueBranch}
          onApplyFilter={() => onApplyFilterPress()}
          categoryRequire={isCategoryRequire}
          dateRangeRequire={isDateRangeRequire}
          branchRequire={isBranchRequire}
          onHandleBranch={(branch_ids?: number[]) =>
            setFastMovingProdParams((prev) => ({ ...prev, branch_ids }))
          }
          onHandlePresetDate={(val?: DateRangeSpecific | string) => {
            setFastMovingProdParams((prev) => ({
              ...prev,
              preset_dates: val,
              // clear date sold params when setting preset dates no matter what
              date_sold_from: undefined,
              date_sold_to: undefined
            }));
          }}
          onHandleSearchKeyword={(keyword?: string) =>
            setSearchKeyword(keyword ?? '')
          }
          onHandleCategoryId={(category_id?: number) =>
            setFastMovingProdParams((prev) => ({ ...prev, category_id }))
          }
          onHandleDateFrom={(date_sold_from?: string | undefined) =>
            setFastMovingProdParams((prev) => ({
              ...prev,
              date_sold_from,
              preset_dates: undefined // clear preset if date is present
            }))
          }
          onHandleDateTo={(date_sold_to?: string | undefined) =>
            setFastMovingProdParams((prev) => ({
              ...prev,
              date_sold_to,
              preset_dates: undefined // clear preset if date is present
            }))
          }
          onHandleErrorClose={handleErrorClose}
          onChangeStatusFilter={(val: StockFilter) => {
            setFastMovingProdParams((prev) => ({
              ...prev,
              with_sales: val === StockFilter.WithSales ? 1 : 0
            }));
          }}
        />

        {isLoading && <LinearProgress />}
        {!fastMovingProductData && (
          <Alert severity="info" style={{ marginTop: '1rem' }}>
            <Typography variant="subtitle2">
              Select category and date sold range to load fast moving products
            </Typography>
          </Alert>
        )}
        {/*TODO: remove later might be useful for improvement of v2 */}
        {/* {fastMovingProductData && fastMovingProductData?.length > 0 && (
          <>
       
            <FastMovingProductTableList
              columnToHide={columnToHide}
              // fastMovingProducts={fastMovingProductData || []}
              fastMovingProducts={tempData || []}
              productsCopy={dataCopy}
              sortFlag={sortFlag}
              setSortFlag={setSortFlag}
              setFastMovingProducts={(val: FastMovingProductsData[]) =>
                setFastMovingProductData(val)
              }
            />
          </>
        )} */}

        {sortedData && sortedData?.length > 0 && !isLoading && (
          <>
            <FastMovingProductSubToolbar
              headerKeys={headerKeys}
              columnIndicesToHide={columnToHide}
              fastMovingProductToCsv={sortedData || []}
              columnToHide={(indices: number[]) => setColumnToHide(indices)}
              onApplyFilterColumn={(columnToShow?: SelectedColumn[]) => {
                setSelectedShowColumn(columnToShow || []);
              }}
              columnTotal={columnTotal}
              categoryId={Number(queryParams?.category_id)}
              queryParams ={queryParams}
            />

            {!error && (
              <FastMovingProductTableV2
                headers={headerKeys}
                fastMovingData={sortedData || []}
                columnToShow={selectedShowColumn || []}
                columnTotal={setColumnTotal}
                queryParams ={queryParams}
              />
            )}
          </>
        )}

        {xs ? (
          <Fab
            color="primary"
            aria-label="add"
            style={{
              position: 'fixed',
              bottom: 16,
              right: 16,
              zIndex: 1000
            }}
            onClick={() => scrollToTopWithOffset()}
          >
            <ArrowUpwardIcon />
          </Fab>
        ) : null}
      </Container>
    </Page>
  );
};
